import { StyleSheet, Document, Page, Text, View, Svg, Path, G, ClipPath, Defs, Rect, Link } from '@react-pdf/renderer'
import React from 'react'
import type {
  FashionOrderDetailMapped,
  ProductApiData,
} from '../../../pages/ClientPanel/FashionOrders/fashionOrderDetailFromApi'
import {
  formatCurrency,
  getTotalPieces,
  compareSizes,
  effectiveItemPrice,
} from '../../../pages/ClientPanel/FashionOrders/fashionOrderDetailFromApi'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 8,
    paddingTop: 30,
    paddingBottom: 40,
    paddingHorizontal: 30,
  },
  pageFrame: {
    position: 'absolute',
    top: 15,
    left: 15,
    right: 15,
    bottom: 15,
    borderWidth: 1,
    borderColor: '#000',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 22,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 8,
    color: '#333',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingBottom: 8,
    marginBottom: 8,
  },
  logoSvg: {
    width: 100,
    height: 40,
  },
  headerTitleContainer: {
    alignItems: 'flex-end',
  },
  brandTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  orderTitle: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  metaTitle: {
    fontSize: 7,
    color: '#333',
    marginTop: 1,
  },
  infoBlock: {
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingBottom: 6,
    marginBottom: 6,
  },
  infoTitle: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: 8,
    marginBottom: 3,
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 2,
  },
  col: {
    flexDirection: 'row',
    marginRight: 15,
    marginBottom: 1,
  },
  boldText: {
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: 8,
    marginTop: 6,
    marginBottom: 4,
  },
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#000',
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#EBEBEB',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    alignItems: 'center',
    paddingVertical: 3,
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    alignItems: 'center',
    paddingVertical: 3,
    paddingHorizontal: 4,
  },
  cellHash: { width: '4%', fontSize: 7 },
  cellCode: { width: '12%', fontSize: 7 },
  cellProd: { width: '32%', fontSize: 7 },
  cellCol: { width: '12%', fontSize: 7 },
  cellQty: { width: '8%', fontSize: 7, textAlign: 'center' },
  cellUnit: { width: '8%', fontSize: 7, textAlign: 'center' },
  cellPriceTab: { width: '12%', fontSize: 7, textAlign: 'right' },
  cellSub: { width: '12%', fontSize: 7, textAlign: 'right' },
  
  kitHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#F0F4F8',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingVertical: 4,
    paddingHorizontal: 6,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  kitTitle: {
    fontSize: 8,
    fontWeight: 'bold',
  },
  kitSummary: {
    fontSize: 7,
  },

  gradeContainer: {
    paddingLeft: 30,
    paddingVertical: 3,
    backgroundColor: '#FCFCFC',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  gradeHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingBottom: 2,
    marginBottom: 2,
  },
  gradeRow: {
    flexDirection: 'row',
    marginBottom: 1,
  },
  gradeColorCell: {
    width: 100,
    fontSize: 7,
    fontWeight: 'bold',
  },
  gradeSizeHeaderCell: {
    width: 30,
    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  gradeSizeQtyCell: {
    width: 30,
    fontSize: 7,
    textAlign: 'center',
  },

  totalsSection: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 6,
  },
  totalsTable: {
    width: 180,
    borderWidth: 1,
    borderColor: '#000',
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
  },

  footerBlock: {
    borderWidth: 1,
    borderColor: '#000',
    padding: 6,
    marginBottom: 6,
  },
  regrasBox: {
    borderWidth: 1,
    borderColor: '#000',
    padding: 6,
    marginTop: 4,
  },
  regrasTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 3,
    textTransform: 'uppercase',
  },
  regrasText: {
    fontSize: 7,
    textAlign: 'center',
    marginBottom: 1,
  },
})

interface BodyFashionOrderProps {
  orderId: number
  detail: FashionOrderDetailMapped | null
  qrCodeDataUrl?: string
  generatedByName?: string
}

const BodyFashionOrder: React.FC<BodyFashionOrderProps> = ({ orderId, detail, qrCodeDataUrl, generatedByName }) => {
  if (!detail) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <View fixed style={styles.pageFrame} />
          <Text style={{ fontSize: 10 }}>Carregando dados do pedido...</Text>
        </Page>
      </Document>
    )
  }

  const { formState, addedProducts, kitCount } = detail

  const formatDateBR = (dateStr: string | undefined | null) => {
    if (!dateStr) return '—'
    try {
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}/${month}/${year}`
    } catch {
      return dateStr
    }
  }

  const standaloneProducts = addedProducts
    .map((p, i) => ({ product: p, index: i }))
    .filter(({ product }) => product.kitIndex === null || product.kitIndex === undefined)

  const kitProducts = (kitIdx: number) =>
    addedProducts
      .map((p, i) => ({ product: p, index: i }))
      .filter(({ product }) => product.kitIndex === kitIdx)

  const renderLogo = () => (
    <Svg width={100} height={40} viewBox="0 0 912 88" style={styles.logoSvg}>
      <Defs>
        <ClipPath id="clip0_173_1426">
          <Rect width={912} height={88} fill="white" />
        </ClipPath>
      </Defs>
      <G clipPath="url(#clip0_173_1426)">
        <Path
          d="M912 44.0616C912 68.3302 890.008 88.0053 862.879 88.0053C835.751 88.0053 813.758 68.3302 813.758 44.0616C813.758 19.7929 835.751 0.117766 862.879 0.117766C890.008 0.117766 912 19.7929 912 44.0616ZM862.879 14.7015C844.311 14.7015 829.259 27.845 829.259 44.0616C829.259 60.2781 844.311 73.4217 862.879 73.4217C881.447 73.4217 896.5 60.2781 896.5 44.0616C896.5 27.845 881.447 14.7015 862.879 14.7015ZM277.723 61.3489C276.373 58.7577 274.196 54.0571 270.362 49.3564C275.293 48.1251 278.49 45.7159 281.239 42.7552C284.647 39.0772 286.969 33.0114 287.061 27.3042C287.126 23.321 286.316 19.7019 284.841 16.5432C283.356 13.3898 281.266 10.7129 278.63 8.57673C276.016 6.46199 272.889 4.83444 269.325 3.73692C265.771 2.63939 261.877 2.08796 257.751 2.08796H209.748V85.7461H225.367V50.1381H240.435C255.763 49.501 258.885 59.8927 260.662 62.152L273.019 85.9013C275.492 85.8585 289.616 85.9013 289.616 85.9013C284.312 74.3532 282.956 71.3926 277.723 61.3543V61.3489ZM225.335 35.533V16.6823H257.632C259.646 16.6823 261.877 16.4575 263.611 16.8323C265.242 17.1856 266.511 17.8227 267.737 18.7435C268.806 19.552 269.687 20.4835 270.405 21.7577C271.096 23.0052 271.582 24.7291 271.415 26.5601C271.091 30.1846 268.709 33.3165 265.566 34.7888C264.221 35.4152 261.531 35.5062 257.686 35.5062H257.664C257.664 35.5062 232.037 35.5223 225.34 35.5276L225.335 35.533ZM0 2.47343L37.8006 86.1583H54.8565L93.2458 2.47343H76.0711L46.3772 68.3302L16.9371 2.47343H0ZM115.838 86.1315H186.124V71.5585H131.457V50.5236H172.822V35.9238H131.457V17.0464H186.124V2.47343H115.838V86.1315ZM518.256 86.1315H588.542V71.5585H533.875V50.5236H575.24V35.9238H533.875V17.0464H588.542V2.47343H518.256V86.1315ZM494.751 60.0854C494.751 56.1879 494.141 52.6222 492.942 49.4635C491.651 46.1495 489.491 43.4619 486.337 41.2455C483.269 39.0933 479.083 37.4336 473.893 36.3146C468.805 35.2171 462.26 34.6657 454.434 34.6657C450.75 34.6657 447.699 34.4194 445.366 33.9269C443.097 33.445 441.299 32.8026 440.008 32.0102C438.798 31.2928 437.967 30.479 437.545 29.5903C437.07 28.6052 436.843 27.6362 436.843 26.6297C436.843 25.136 437.264 23.6583 438.102 22.2289C438.933 20.7994 440.138 19.4877 441.672 18.3367C443.27 17.1642 445.096 16.2433 447.105 15.6062C449.26 14.921 451.463 14.589 453.84 14.589C456.524 14.589 459.078 14.7496 461.428 15.0602C463.648 15.36 465.813 15.8793 467.866 16.6127C469.929 17.3516 471.992 18.3152 473.996 19.4877C476.113 20.7512 478.338 22.2289 480.601 23.8832L481.562 24.5845L491.359 14.0429L490.371 13.1489C484.927 8.2448 479.132 4.78626 473.142 2.86961C467.191 0.958309 460.699 -0.00537109 453.84 -0.00537109C448.941 -0.00537109 444.367 0.738804 440.262 2.20038C436.119 3.66732 432.501 5.64286 429.514 8.07883C426.538 10.5041 424.151 13.3844 422.412 16.6449C420.695 19.9 419.825 23.3103 419.825 26.7742C419.825 34.216 422.828 39.8856 428.763 43.6333C434.515 47.2203 442.951 49.0352 453.84 49.0352C456.783 49.0352 459.953 49.1102 463.27 49.2601C466.289 49.3939 469.011 49.8329 471.36 50.5664C473.58 51.257 475.411 52.3599 476.804 53.8429C478.111 55.2349 478.77 57.3336 478.77 60.0801C478.77 62.3126 478.127 64.1757 476.799 65.7925C475.47 67.4415 473.623 68.8495 471.311 69.9792C469.011 71.1035 466.294 71.9547 463.226 72.5062C460.191 73.0737 457.037 73.3628 453.84 73.3628C450.642 73.3628 447.461 72.9612 444.766 72.1689C441.936 71.3123 439.538 70.3165 437.426 69.1226C435.336 67.9715 433.441 66.6384 431.788 65.1715C430.27 63.8223 428.866 62.4464 427.608 61.0866L426.738 60.1389L415.504 69.7597L416.352 70.7555C418.577 73.3574 421.186 75.788 424.108 77.9777C426.976 80.1246 429.995 81.8913 433.322 83.3797C436.465 84.8252 439.851 85.9602 443.378 86.7633C446.738 87.561 450.259 87.9625 453.834 87.9625C459.489 87.9625 464.879 87.3254 469.848 86.0726C474.773 84.8252 479.121 82.9942 482.772 80.6439C486.466 78.2615 489.404 75.3062 491.494 71.8744C493.639 68.3945 494.757 64.3684 494.757 60.0747L494.751 60.0854ZM796.378 60.0854C796.378 56.1879 795.768 52.6222 794.569 49.4635C793.278 46.1495 791.118 43.4619 787.964 41.2455C784.896 39.0933 780.71 37.4336 775.52 36.3146C770.433 35.2171 763.887 34.6657 756.061 34.6657C752.378 34.6657 749.326 34.4194 746.993 33.9269C744.725 33.445 742.926 32.8026 741.635 32.0102C740.425 31.2928 739.594 30.479 739.172 29.5903C738.697 28.6052 738.47 27.6362 738.47 26.6297C738.47 25.136 738.892 23.6583 739.729 22.2289C740.56 20.7994 741.765 19.4877 743.299 18.3367C744.897 17.1642 746.723 16.2433 748.732 15.6062C750.887 14.921 753.09 14.589 755.467 14.589C758.151 14.589 760.706 14.7496 763.055 15.0602C765.275 15.36 767.441 15.8793 769.493 16.6127C771.556 17.3516 773.619 18.3152 775.623 19.4877C777.74 20.7512 779.965 22.2289 782.228 23.8832L783.189 24.5845L792.987 14.0429L791.998 13.1489C786.554 8.2448 780.759 4.78626 774.77 2.86961C768.818 0.958309 762.326 -0.00537109 755.467 -0.00537109C750.568 -0.00537109 745.994 0.738804 741.889 2.20038C737.747 3.66732 734.128 5.64286 731.141 8.07883C728.165 10.5041 725.778 13.3844 724.039 16.6449C722.322 19.9 721.452 23.3103 721.452 26.7742C721.452 34.216 724.455 39.8856 730.391 43.6333C736.143 47.2203 744.579 49.0352 755.467 49.0352C758.41 49.0352 761.581 49.1102 764.897 49.2601C767.916 49.3939 770.638 49.8329 772.987 50.5664C775.207 51.257 777.038 52.3599 778.431 53.8429C779.738 55.2349 780.397 57.3336 780.397 60.0801C780.397 62.3126 779.755 64.1757 778.426 65.7925C777.097 67.4415 775.25 68.8495 772.939 69.9792C770.638 71.1035 767.921 71.9547 764.854 72.5062C761.818 73.0737 758.664 73.3628 755.467 73.3628C752.27 73.3628 749.088 72.9612 746.393 72.1689C743.563 71.3123 741.165 70.3165 739.054 69.1226C736.964 67.9715 735.068 66.6384 733.415 65.1715C731.897 63.8223 730.493 62.4464 729.235 61.0866L728.365 60.1389L717.132 69.7597L717.979 70.7555C720.205 73.3574 722.813 75.788 725.735 77.9777C728.603 80.1246 731.622 81.8913 734.949 83.3797C738.092 84.8252 741.479 85.9602 745.005 86.7633C748.365 87.561 751.886 87.9625 755.461 87.9625C761.116 87.9625 766.506 87.3254 771.475 86.0726C776.401 84.8252 780.748 82.9942 784.399 80.6439C788.093 78.2615 791.032 75.3062 793.122 71.8744C795.266 68.3945 796.384 64.3684 796.384 60.0747L796.378 60.0854ZM681.513 62.3126L672.537 52.558L626.932 2.47343H612.193V86.1583H627.812V26.3191L636.788 36.0791L681.999 85.7246L682.393 86.1583H697.132V2.47343H681.513V62.3126ZM402.472 44.0616C402.472 68.3302 380.48 88.0053 353.351 88.0053C326.223 88.0053 304.23 68.3302 304.23 44.0616C304.23 19.7929 326.223 0.117766 353.351 0.117766C380.48 0.117766 402.472 19.7929 402.472 44.0616ZM353.351 14.7015C334.783 14.7015 319.731 27.845 319.731 44.0616C319.731 60.2781 334.783 73.4217 353.351 73.4217C371.919 73.4217 386.972 60.2781 386.972 44.0616C386.972 27.845 371.919 14.7015 353.351 14.7015Z"
          fill="black"
        />
      </G>
    </Svg>
  )

  /* O unitário derivado nem sempre é redondo (1.146,06 ÷ 4 = 286,515): com duas casas, quantidade ×
     preço não fecha com o subtotal impresso ao lado. */
  const formatUnitPrice = (value: number): string => {
    const redondo = Math.abs(value * 100 - Math.round(value * 100)) < 1e-9
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: redondo ? 2 : 4,
    })
  }

  const renderProductGrade = (product: ProductApiData) => {
    if (!product.items || product.items.length === 0) return null

    const sizes = Array.from(new Set(product.items.map((i) => i.size).filter(Boolean))).sort(compareSizes)
    const colorIds = Array.from(new Set(product.items.map((i) => i.colorId)))

    const colorIdToName: { [id: number]: string } = {}
    if (product.selectedColors && product.colors) {
      const colorsList = product.colors
      product.selectedColors.forEach((id: number, idx: number) => {
        colorIdToName[id] = colorsList[idx] || `Cor #${id}`
      })
    }
    product.items.forEach((item) => {
      if (!colorIdToName[item.colorId]) {
        colorIdToName[item.colorId] = `Cor #${item.colorId}`
      }
    })

    return (
      <View style={styles.gradeContainer} wrap={false}>
        <View style={styles.gradeHeaderRow}>
          <Text style={styles.gradeColorCell}>Grade / Cor</Text>
          {sizes.map((size) => (
            <Text key={size} style={styles.gradeSizeHeaderCell}>{size}</Text>
          ))}
        </View>
        {colorIds.map((colorId) => (
          <View key={colorId} style={styles.gradeRow}>
            <Text style={styles.gradeColorCell}>{colorIdToName[colorId]}</Text>
            {sizes.map((size) => {
              const qty = product.items.find((i) => i.colorId === colorId && i.size === size)?.quantity || 0
              return (
                <Text key={size} style={styles.gradeSizeQtyCell}>
                  {qty > 0 ? String(qty) : '-'}
                </Text>
              )
            })}
          </View>
        ))}
      </View>
    )
  }

  const renderProductRow = (product: ProductApiData, idx: number) => {
    const code = product.fashionProductId || '-'
    const name = product.fashionProduct?.name || '-'
    const totalPieces = getTotalPieces(product)
    const family = product.fashionProduct?.group?.family?.name || product.family?.name || '-'
    const subtotal = effectiveItemPrice(product)
    const unitario = totalPieces > 0 ? subtotal / totalPieces : 0

    return (
      <View key={idx} wrap={false}>
        <View style={styles.tableRow}>
          <Text style={styles.cellHash}>{idx + 1}</Text>
          <Text style={styles.cellCode}>{code}</Text>
          <Text style={styles.cellProd}>{name} ({family})</Text>
          <Text style={styles.cellCol}>{product.collectionName || '—'}</Text>
          <Text style={styles.cellQty}>{totalPieces}</Text>
          <Text style={styles.cellUnit}>UN</Text>
          <Text style={styles.cellPriceTab}>{formatUnitPrice(unitario)}</Text>
          <Text style={styles.cellSub}>{formatCurrency(subtotal)}</Text>
        </View>
        {renderProductGrade(product)}
      </View>
    )
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View fixed style={styles.pageFrame} />
        
        {/* Cabeçalho */}
        <View fixed style={styles.header}>
          {renderLogo()}
          <View style={styles.headerTitleContainer}>
            <Text style={styles.orderTitle}>
              {(() => {
                const displayNumber = formState.saleStatus === 'Fechado' && formState.saleNumber != null
                  ? `Venda Nº ${formState.saleNumber}`
                  : formState.proposalNumber != null
                    ? `Proposta Nº ${formState.proposalNumber}`
                    : `Pedido Nº ${orderId}`
                return formState.clientOrderNumber
                  ? `${displayNumber} / Pedido Cliente Nº ${formState.clientOrderNumber}`
                  : displayNumber
              })()}
            </Text>
            <Text style={styles.metaTitle}>
              {(() => {
                const now = new Date()
                const dateStr = now.toLocaleDateString('pt-BR')
                const timeStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                return `${dateStr} - ${timeStr}`
              })()}
            </Text>
            {generatedByName && (
              <Text style={styles.metaTitle}>{generatedByName.toUpperCase()}</Text>
            )}
          </View>
        </View>

        <Text fixed style={styles.pageNumber} render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />

        {/* Representada */}
        <View style={styles.infoBlock}>
          <Text style={styles.infoTitle}>Representada</Text>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.boldText}>Fornecedor: </Text>
              <Text>{formState.supplier.companyName || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.boldText}>CNPJ: </Text>
              <Text>{formState.supplier.cnpj || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.boldText}>Inscrição Estadual: </Text>
              <Text>{(formState.supplier as any).inscricaoEstadual || '—'}</Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.boldText}>Telefone: </Text>
              <Text>{formState.supplier.telefone || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.boldText}>E-mail: </Text>
              <Text>{formState.supplier.email || '—'}</Text>
            </View>
          </View>
        </View>

        {/* Cliente */}
        <View style={styles.infoBlock}>
          <Text style={styles.infoTitle}>Cliente</Text>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.boldText}>Cliente: </Text>
              <Text>{formState.company.companyName || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.boldText}>Nome Fantasia: </Text>
              <Text>{formState.company.nomeFantasia || '—'}</Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.boldText}>CNPJ: </Text>
              <Text>{formState.company.cnpj || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.boldText}>Inscrição Estadual: </Text>
              <Text>{formState.company.inscricaoEstadual || '—'}</Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.boldText}>Endereço: </Text>
              <Text>{formState.company.endereco || '—'}</Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.boldText}>Bairro: </Text>
              <Text>{formState.company.bairro || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.boldText}>CEP: </Text>
              <Text>{formState.company.cep || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.boldText}>Cidade: </Text>
              <Text>{formState.company.cidade || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.boldText}>Estado: </Text>
              <Text>{formState.company.estado || '—'}</Text>
            </View>
          </View>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.boldText}>Telefone: </Text>
              <Text>{formState.company.telefone || '—'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.boldText}>E-mail: </Text>
              <Text>{formState.company.email || '—'}</Text>
            </View>
          </View>
        </View>

        {/* Detalhes do Pedido */}
        <View style={styles.infoBlock}>
          <Text style={styles.infoTitle}>Detalhes do Pedido</Text>
          <View style={styles.row}>
            {formState.orderType ? (
              <View style={styles.col}>
                <Text style={styles.boldText}>Tipo de pedido: </Text>
                <Text>{formState.orderType}</Text>
              </View>
            ) : null}
            {formState.seller?.name ? (
              <View style={styles.col}>
                <Text style={styles.boldText}>Vendedor: </Text>
                <Text>{formState.seller.name}</Text>
              </View>
            ) : null}
          </View>
          {(formState.representative || formState.orderSituation) ? (
            <View style={styles.row}>
              {formState.representative ? (
                <View style={styles.col}>
                  <Text style={styles.boldText}>Representante: </Text>
                  <Text>{formState.representative}</Text>
                </View>
              ) : null}
              {formState.orderSituation ? (
                <View style={styles.col}>
                  <Text style={styles.boldText}>Situação do Pedido: </Text>
                  <Text>{formState.orderSituation}</Text>
                </View>
              ) : null}
            </View>
          ) : null}
          {(formState.generalClass || formState.orderDate) ? (
            <View style={styles.row}>
              {formState.generalClass ? (
                <View style={styles.col}>
                  <Text style={styles.boldText}>Classe Geral: </Text>
                  <Text>{formState.generalClass}</Text>
                </View>
              ) : null}
              {formState.orderDate ? (
                <View style={styles.col}>
                  <Text style={styles.boldText}>Data do Pedido: </Text>
                  <Text>{formatDateBR(formState.orderDate)}</Text>
                </View>
              ) : null}
            </View>
          ) : null}
          {formState.maximumBillingAmount ? (
            <View style={styles.row}>
              <View style={styles.col}>
                <Text style={styles.boldText}>Valor máx. faturamento: </Text>
                <Text>{formState.maximumBillingAmount}</Text>
              </View>
            </View>
          ) : null}
          {detail?.attachments && detail.attachments.length > 0 ? (
            <View style={styles.row}>
              <View style={styles.col}>
                <Text style={styles.boldText}>Anexos: </Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                  {detail.attachments.map((att, index) => (
                    <Link key={index} src={att.filePath} style={{ fontSize: 7, color: '#0066cc', textDecoration: 'underline', marginRight: 8 }}>
                      {att.originalName || att.fileName || 'Ver Anexo'}
                    </Link>
                  ))}
                </View>
              </View>
            </View>
          ) : null}
        </View>

        {/* Tabela de Itens */}
        <Text style={styles.sectionTitle}>Orçamento de Itens</Text>
        <View style={styles.table}>
          {/* Cabeçalho da Tabela Principal */}
          <View style={styles.tableHeader}>
            <Text style={[styles.boldText, styles.cellHash]}>#</Text>
            <Text style={[styles.boldText, styles.cellCode]}>Código</Text>
            <Text style={[styles.boldText, styles.cellProd]}>Produto</Text>
            <Text style={[styles.boldText, styles.cellCol]}>Coleção</Text>
            <Text style={[styles.boldText, styles.cellQty]}>Qtde.</Text>
            <Text style={[styles.boldText, styles.cellUnit]}>Unidade</Text>
            <Text style={[styles.boldText, styles.cellPriceTab]}>Preço unitário</Text>
            <Text style={[styles.boldText, styles.cellSub]}>Subtotal</Text>
          </View>

          {/* Produtos Avulsos */}
          {standaloneProducts.map(({ product }, idx) =>
            renderProductRow(product, idx)
          )}

          {/* Produtos em Conjuntos */}
          {Array.from({ length: kitCount }, (_, kitIdx) => {
            const kits = kitProducts(kitIdx)
            if (kits.length === 0) return null

            const kitTotalPieces = kits.reduce((sum, { product }) => sum + getTotalPieces(product), 0)
            const kitTotal = kits.reduce((sum, { product }) => sum + effectiveItemPrice(product), 0)

            return (
              <View key={`kit-${kitIdx}`}>
                {/* Linha do Conjunto */}
                <View style={styles.kitHeaderRow}>
                  <Text style={styles.kitTitle}>Conjunto {kitIdx + 1}</Text>
                  <Text style={styles.kitSummary}>
                    Itens: {kits.length} | Peças: {kitTotalPieces} | Total: {formatCurrency(kitTotal)}
                  </Text>
                </View>

                {/* Sub-produtos do Conjunto */}
                {kits.map(({ product }, subIdx) =>
                  renderProductRow(product, subIdx)
                )}
              </View>
            )
          })}
        </View>

        {/* Totais do Pedido */}
        <View style={styles.totalsSection} wrap={false}>
          <View style={styles.totalsTable}>
            <View style={styles.totalsRow}>
              <Text style={styles.boldText}>Qtde. Total:</Text>
              <Text>{formState.quantityPieces}</Text>
            </View>
            <View style={styles.totalsRow}>
              <Text style={styles.boldText}>Valor do frete:</Text>
              <Text>{formState.shippingCost || 'R$ 0,00'}</Text>
            </View>
            <View style={[styles.totalsRow, { borderBottomWidth: 0 }]}>
              <Text style={styles.boldText}>Valor total:</Text>
              <Text>{formState.sellingPriceLabel || formState.totalCost}</Text>
            </View>
          </View>
        </View>

        {/* Informações Finais e Regras */}
        <View wrap={false}>
          {/* Informações de Pagamento */}
          {(formState.paymentCondition || formState.paymentMethod || formState.billingDate || (formState.installments && formState.installments.length > 0)) ? (
            <View style={styles.footerBlock}>
              <Text style={[styles.boldText, { fontSize: 8, marginBottom: 4, textTransform: 'uppercase', borderBottomWidth: 0.5, borderBottomColor: '#ccc', paddingBottom: 2 }]}>
                Informações de Pagamento
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', width: '100%' }}>
                {formState.paymentMethod ? (
                  <View style={{ width: '33%', marginBottom: 4 }}>
                    <Text style={styles.boldText}>Forma de Pagamento: </Text>
                    <Text style={{ fontSize: 7 }}>{formState.paymentMethod}</Text>
                  </View>
                ) : null}
                {formState.paymentCondition ? (
                  <View style={{ width: '33%', marginBottom: 4 }}>
                    <Text style={styles.boldText}>Condição de Pagamento: </Text>
                    <Text style={{ fontSize: 7 }}>{formState.paymentCondition}</Text>
                  </View>
                ) : null}
                {formState.billingDate ? (
                  <View style={{ width: '33%', marginBottom: 4 }}>
                    <Text style={styles.boldText}>Data de Faturamento: </Text>
                    <Text style={{ fontSize: 7 }}>{formatDateBR(formState.billingDate)}</Text>
                  </View>
                ) : null}
              </View>

              {/* Parcelas */}
              {formState.installments && formState.installments.length > 0 ? (
                <View style={{ marginTop: 4, borderTopWidth: 0.5, borderTopColor: '#eee', paddingTop: 4 }}>
                  <Text style={[styles.boldText, { fontSize: 7, marginBottom: 2 }]}>Parcelas:</Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                    {formState.installments.map((inst, index) => (
                      <View key={index} style={{ width: '23%', flexDirection: 'row', justifyContent: 'space-between', paddingRight: 6, paddingVertical: 1 }}>
                        <Text style={{ fontSize: 7, color: '#555' }}>{`${inst.numero}ª:`}</Text>
                        <Text style={[styles.boldText, { fontSize: 7 }]}>{formatCurrency(inst.valor)}</Text>
                        <Text style={{ fontSize: 7, color: '#555' }}>{formatDateBR(inst.vencimento)}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              ) : null}
            </View>
          ) : null}

          {/* Informações de Entrega */}
          {(formState.shippingType || formState.carrier?.companyName || formState.shippingCost || formState.trackingCode || formState.finalDateOfDelivery) ? (
            <View style={styles.footerBlock}>
              <Text style={[styles.boldText, { fontSize: 8, marginBottom: 4, textTransform: 'uppercase', borderBottomWidth: 0.5, borderBottomColor: '#ccc', paddingBottom: 2 }]}>
                Informações de Entrega
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', width: '100%' }}>
                {formState.shippingType ? (
                  <View style={{ width: '20%', marginBottom: 4 }}>
                    <Text style={styles.boldText}>Tipo de frete: </Text>
                    <Text style={{ fontSize: 7 }}>{formState.shippingType}</Text>
                  </View>
                ) : null}
                {formState.carrier?.companyName ? (
                  <View style={{ width: '20%', marginBottom: 4 }}>
                    <Text style={styles.boldText}>Transportadora: </Text>
                    <Text style={{ fontSize: 7 }}>{formState.carrier.companyName}</Text>
                  </View>
                ) : null}
                {formState.shippingCost ? (
                  <View style={{ width: '20%', marginBottom: 4 }}>
                    <Text style={styles.boldText}>Valor do frete: </Text>
                    <Text style={{ fontSize: 7 }}>{formState.shippingCost}</Text>
                  </View>
                ) : null}
                {formState.trackingCode ? (
                  <View style={{ width: '20%', marginBottom: 4 }}>
                    <Text style={styles.boldText}>Código de rastreio: </Text>
                    <Text style={{ fontSize: 7 }}>{formState.trackingCode}</Text>
                  </View>
                ) : null}
                {formState.finalDateOfDelivery ? (
                  <View style={{ width: '20%', marginBottom: 4 }}>
                    <Text style={styles.boldText}>Prazo de entrega: </Text>
                    <Text style={{ fontSize: 7 }}>{formatDateBR(formState.finalDateOfDelivery)}</Text>
                  </View>
                ) : null}
              </View>
            </View>
          ) : null}

          {/* Informações Adicionais */}
          {formState.extraInformation ? (
            <View style={styles.footerBlock}>
              <Text style={styles.boldText}>Informações Adicionais:</Text>
              <Text style={{ marginTop: 2, fontSize: 8 }}>{formState.extraInformation}</Text>
            </View>
          ) : null}

          {/* Regras Verosenso */}
          <View style={styles.regrasBox}>
            <Text style={styles.regrasTitle}>REGRAS VEROSENSO</Text>
            <Text style={styles.regrasText}>- Cancelamentos ou alterações serão aceitos somente após 07 dias úteis a partir de hoje.</Text>
            <Text style={styles.regrasText}>- O prazo para conferência da mercadoria, quando entregue na loja, será de 03 dias úteis.</Text>
            <Text style={styles.regrasText}>- O Markup mínimo aplicado na venda deve ser de 2.3.</Text>
          </View>
        </View>

      </Page>
    </Document>
  )
}

export default BodyFashionOrder

